import { useState } from "react";
import {
  Button,
  DataTable,
  Dialog,
  Field,
  PageHead,
  Panel,
  PersonCell,
  Pill,
  StatusPill,
  Tabs,
  Textarea,
  type Column,
} from "@guestpilot/ui-kit";
import { useGet, useWrite } from "../api/hooks";
import type { Schema } from "../api/types";
import { useHandleApiError } from "../errors";
import { formatDate } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";

type Exception = Schema<"ExceptionRecord">;
const STATUSES = ["pending_approval", "active", "expired", "rejected", "revoked"] as const;

/** Time-boxed exceptions: an expired one is a risk again (BR-092…BR-097); an Admin may approve their own proposal (D-138). */
export function Exceptions() {
  const { t, locale } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const list = useGet<Exception[]>("/api/v1/exceptions");
  const [tab, setTab] = useState<string>("pending_approval");
  const [dialog, setDialog] = useState<{ e: Exception; kind: "approve" | "reject" | "revoke" } | null>(null);
  const [reason, setReason] = useState("");
  const invalidate = [
    "/api/v1/exceptions",
    "/api/v1/dashboard",
    "/api/v1/participants",
    "/api/v1/teams",
    "/api/v1/campaigns",
  ];
  const approve = useWrite("POST", "/api/v1/exceptions/:id/approve", invalidate);
  const rejectM = useWrite<unknown, Schema<"ExceptionDecision">>(
    "POST",
    "/api/v1/exceptions/:id/reject",
    invalidate,
  );
  const revoke = useWrite<unknown, Schema<"ExceptionDecision">>(
    "POST",
    "/api/v1/exceptions/:id/revoke",
    invalidate,
  );
  const tz = s.me.tenant.timeZone;
  const rows = list.data;
  const count = (st: string) => (rows ?? []).filter((r) => r.status === st).length;
  const shown = (rows ?? []).filter((r) => r.status === tab);
  const gate = s.gate("EXCEPTION_APPROVE");

  const columns: Column<Exception>[] = [
    {
      key: "person",
      header: t("ui:users.col.person"),
      render: (e) => (
        <PersonCell
          name={e.participant?.displayName || e.participant?.email || "—"}
          sub={e.participant?.displayName ? e.participant.email : undefined}
        />
      ),
    },
    { key: "team", header: t("ui:users.col.team"), render: (e) => <Pill>{e.team?.displayName ?? "—"}</Pill> },
    {
      key: "reason",
      header: t("ui:removals.col.reason"),
      render: (e) => <span title={e.reason}>{e.reason}</span>,
    },
    {
      key: "proposed",
      header: t("ui:exceptions.col.proposed"),
      render: (e) => e.proposedBy?.displayName ?? "—",
    },
    {
      key: "approved",
      header: t("ui:exceptions.col.approved"),
      render: (e) => e.approvedBy?.displayName ?? (e.approvalRequired ? t("ui:exceptions.needsAdmin") : "—"),
    },
    {
      key: "until",
      header: t("ui:exceptions.col.until"),
      render: (e) => formatDate(e.validUntil, locale, tz),
    },
    {
      key: "status",
      header: t("ui:users.col.status"),
      render: (e) => (
        <StatusPill
          domain="exception"
          value={e.status ?? "active"}
          label={
            e.status === "expired" ? t("ui:exceptions.expiredBack") : t(`ui:exceptionStatus.${e.status}`)
          }
        />
      ),
    },
    {
      key: "actions",
      header: t("ui:table.actions"),
      render: (e) => (
        <div className="gp-row">
          {e.status === "pending_approval" ? (
            <>
              <Button
                variant="primary"
                disabledReason={gate}
                onClick={() => setDialog({ e, kind: "approve" })}
              >
                {t("ui:exceptions.approve")}
              </Button>
              <Button variant="ghost" disabledReason={gate} onClick={() => setDialog({ e, kind: "reject" })}>
                {t("ui:exceptions.reject")}
              </Button>
            </>
          ) : null}
          {e.status === "active" ? (
            <Button variant="ghost" disabledReason={gate} onClick={() => setDialog({ e, kind: "revoke" })}>
              {t("ui:exceptions.revoke")}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const run = () => {
    if (!dialog) return;
    const params = { id: dialog.e.id ?? "" };
    const done = {
      onSuccess: () => {
        setDialog(null);
        setReason("");
      },
      onError: fail,
    };
    if (dialog.kind === "approve") approve.mutate({ params }, done);
    else if (dialog.kind === "reject") rejectM.mutate({ params, body: { reason } }, done);
    else revoke.mutate({ params, body: { reason } }, done);
  };

  return (
    <div className="gp-page">
      <PageHead title={t("ui:exceptions.title")} subtitle={t("ui:exceptions.lead")} />
      <Tabs
        label={t("ui:exceptions.title")}
        value={tab}
        onChange={setTab}
        tabs={STATUSES.map((st) => ({ key: st, label: t(`ui:exceptionStatus.${st}`), count: count(st) }))}
      />
      <Panel label={t("ui:exceptions.title")}>
        <DataTable
          caption={t("ui:exceptions.title")}
          columns={columns}
          rows={rows ? shown : undefined}
          loading={list.isLoading}
          error={list.isError}
          onRetry={() => void list.refetch()}
          errorTitle={t("ui:table.error")}
          retryLabel={t("common:retry")}
          rowKey={(e) => e.id ?? ""}
          emptyTitle={t("ui:exceptions.empty")}
        />
      </Panel>
      <Dialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        title={dialog ? t(`ui:exceptions.dialog.${dialog.kind}.title`) : ""}
        description={dialog ? t(`ui:exceptions.dialog.${dialog.kind}.body`) : undefined}
        footer={
          <>
            <Button onClick={() => setDialog(null)}>{t("common:cancel")}</Button>
            <Button
              variant={dialog?.kind === "approve" ? "primary" : "danger"}
              disabledReason={
                dialog && dialog.kind !== "approve" && !reason.trim()
                  ? t("ui:removal.reasonRequired")
                  : undefined
              }
              onClick={run}
            >
              {dialog ? t(`ui:exceptions.${dialog.kind}`) : ""}
            </Button>
          </>
        }
      >
        {dialog && dialog.kind !== "approve" ? (
          <Field label={t("ui:removals.rejectReason")}>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        ) : null}
      </Dialog>
    </div>
  );
}
